import {
  CellClassParams,
  ColDef,
  ExcelStyle,
  GridApi,
  GridOptions,
  Module,
} from '@ag-grid-community/core'
import { ExcelExportModule } from '@ag-grid-enterprise/excel-export'
import { RangeSelectionModule } from '@ag-grid-enterprise/range-selection'
import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  OnDestroy,
} from '@angular/core'
import {
  enableHeliosCopyBehavior,
  enableHeliosGridDensityLevel,
  heliosClientSideRowConfig,
} from '@helios/ag-grid-config'
import { DensityLevel, UserPreferencesService } from '@helios/user-preferences'
import {
  BehaviorSubject,
  combineLatest,
  concat,
  Observable,
  of,
  Subject,
} from 'rxjs'
import {
  catchError,
  distinctUntilChanged,
  map,
  skip,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators'
import { SourceFieldCatalog } from '../../models/field-catalog.model'
import {
  DetailField,
  RowDetailSource,
  rowDetailSourceOf,
} from '../../models/row-detail.model'
import { WorkbenchRow } from '../../models/workbench-row.model'
import {
  DetailGridContext,
  detailStatusBarPanel,
  DetailStatusBarModule,
} from './position-detail-status-bar'
import { FieldCatalogService } from '../../services/field-catalog.service'
import { RowDetailService } from '../../services/row-detail.service'
import {
  AnchorSelection,
  WorkbenchStoreService,
} from '../../services/workbench-store.service'
import {
  SubPanelEmptyState,
  subPanelEmptyState,
  subPanelMultipleSelectedState,
} from '../sub-panel-tab.model'
import {
  allGroupsShown,
  buildDetailFields,
  compareDetailCells,
  defaultDetailFilters,
  DetailFieldSet,
  DetailFilterState,
  filterDetailFields,
  groupsButtonLabel,
} from './position-detail.view-model'

/** The mutually-exclusive states the tab can be in once the anchor has been resolved. */
interface DetailViewModel {
  /** Present when 0 (No Position Selected) or >1 (Multiple Positions Selected) rows are selected. */
  emptyState: SubPanelEmptyState | null
  /** The row-detail request is in flight. */
  loading: boolean
  /** The request failed — an inline strip, never a modal or a thrown error. */
  error: boolean
  /** The anchor is a Clearwater row, which has no row-detail endpoint. */
  unavailable: boolean
  /** Present once a response has been transposed. `total: 0` is a legitimate empty response. */
  fieldSet: DetailFieldSet | null
}

const inertVm = (): DetailViewModel => ({
  emptyState: null,
  loading: false,
  error: false,
  unavailable: false,
  fieldSet: null,
})

const cardVm = (emptyState: SubPanelEmptyState): DetailViewModel => ({
  ...inertVm(),
  emptyState,
})

const loadingVm = (): DetailViewModel => ({ ...inertVm(), loading: true })

const errorVm = (): DetailViewModel => ({ ...inertVm(), error: true })

const unavailableVm = (): DetailViewModel => ({
  ...inertVm(),
  unavailable: true,
})

/** Everything the template renders, after the toolbar's filters have been applied. */
export interface DetailDisplayViewModel {
  emptyState: SubPanelEmptyState | null
  loading: boolean
  error: boolean
  unavailable: boolean
  /** A legitimate 200 that carried no fields for this row. */
  noData: boolean
  /** True when a row is loaded and has fields: the toolbar, table and footer all render. */
  loaded: boolean
  /** The rows currently on screen. */
  rows: DetailField[]
  /** Every group present in the response, in response order — the dropdown's menu. */
  groups: string[]
  hiddenGroups: ReadonlySet<string>
  /** The dropdown button's text: `All Groups` / the group's name / `N Groups`. */
  groupsLabel: string
  /** Drives the menu's `Select All` checkbox. */
  allGroupsShown: boolean
  hideEmpty: boolean
  search: string
  /** Footer `Fields:` — how many rows are currently shown. */
  shownCount: number
  /** Footer `Total:` — how many fields the row has, before any filter. */
  total: number
}

const buildDisplayVm = (
  vm: DetailViewModel,
  filters: DetailFilterState,
): DetailDisplayViewModel => {
  const fieldSet = vm.fieldSet
  const rows = fieldSet ? filterDetailFields(fieldSet.fields, filters) : []
  const groups = fieldSet?.groups ?? []
  return {
    emptyState: vm.emptyState,
    loading: vm.loading,
    error: vm.error,
    unavailable: vm.unavailable,
    noData: fieldSet != null && fieldSet.total === 0,
    loaded: fieldSet != null && fieldSet.total > 0,
    rows,
    groups,
    hiddenGroups: filters.hiddenGroups,
    groupsLabel: groupsButtonLabel(groups, filters.hiddenGroups),
    allGroupsShown: allGroupsShown(groups, filters.hiddenGroups),
    hideEmpty: filters.hideEmpty,
    search: filters.search,
    shownCount: rows.length,
    total: fieldSet?.total ?? 0,
  }
}

/** Stable key for the anchor, so the toolbar only resets when the position genuinely changed. */
const anchorKeyOf = (anchor: AnchorSelection): string =>
  anchor.kind === 'single' ? `single:${anchor.row.rowId}` : anchor.kind

/** The exported workbook's one worksheet. */
export const EXPORT_SHEET_NAME = 'Position Detail'

/** Excel-export cell classes. They carry no screen styling — `excelStyles` matches them by id. */
export const XL_TEXT_CLASS = 'detail-xl-text'
export const XL_NUMBER_CLASS = 'detail-xl-number'

/**
 * Pins each exported cell's type. Without these, ag-Grid 26's xlsx writer types a cell from the
 * shape of its value alone, so every numeric-looking string would become a Number cell — the very
 * re-typing the move off CSV exists to stop.
 */
const DETAIL_EXCEL_STYLES: ExcelStyle[] = [
  { id: XL_TEXT_CLASS, dataType: 'String' },
  { id: XL_NUMBER_CLASS, dataType: 'Number' },
]

/**
 * The Value column's per-row classes: the screen class plus the export type the view model decided
 * (`DetailField.numeric`). A missing row (`data` undefined) falls back to Text — the safe type.
 */
export const detailValueCellClass = (
  params: Pick<CellClassParams, 'data'>,
): string[] => {
  const field = params.data as DetailField | undefined
  return ['detail-cell', field?.numeric ? XL_NUMBER_CLASS : XL_TEXT_CLASS]
}

/**
 * Position Detail sub-panel — the 2.0 replacement for the 1.0 External Position Detail tab,
 * generalized from "external rows only" to any source's selected row and driven by the row-detail
 * API rather than by transposing the in-memory grid row.
 *
 * Driven by {@link WorkbenchStoreService.anchorSelection}, NOT by `anchorRow()`: this tab shows
 * detail for *the* single selected position, so the "exactly one selected row" rule lives in the
 * store (where the sibling sub-panels can reuse it) rather than being re-invented here. The store
 * owns which row is the anchor; this component owns only the fetch lifecycle and the presentation.
 *
 * All derivation — group-label resolution, value formatting, empty detection, the three filters and
 * the sort comparator — lives in the pure `position-detail.view-model.ts` and is unit-tested there
 * without a TestBed.
 */
@Component({
  selector: 'hs-position-detail',
  templateUrl: './position-detail.component.html',
  styleUrls: ['./position-detail.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PositionDetailComponent implements OnDestroy {
  readonly vm$: Observable<DetailViewModel>
  readonly display$: Observable<DetailDisplayViewModel>

  /**
   * Range selection (and therefore the copy behaviour built on it) needs its own ag-Grid module;
   * the status-bar module carries the `Fields` / `Total` panels in the grid's own footer.
   *
   * ExcelExportModule backs the kebab's `Export to Excel` (`exportDataAsExcel`). It is listed here
   * explicitly even though the host table may already register it: nothing in this repo was found
   * to register it, ag-Grid de-duplicates a module registered twice, and without it the export is a
   * silent no-op rather than an error.
   */
  readonly gridModules: Module[] = [
    RangeSelectionModule,
    ExcelExportModule,
    DetailStatusBarModule,
  ]

  /** Mutated in place — the two status panels read it through `gridOptions.context`. */
  private readonly gridContext: DetailGridContext = { fields: 0, total: 0 }

  readonly gridOptions: GridOptions = this.createGridOptions()

  private gridApi?: GridApi

  /**
   * Identifies the loaded position in the exported file's name. Captured when the fetch is issued
   * rather than read back off the store at export time: both of the store's anchor accessors are
   * Observables, and the export has to name the row the table is actually showing — the one the
   * last fetch resolved.
   */
  private exportTarget?: { source: RowDetailSource; rowId: string }

  private readonly filters$ = new BehaviorSubject<DetailFilterState>(
    defaultDetailFilters(),
  )
  private readonly destroy$ = new Subject<void>()

  constructor(
    @Inject(RowDetailService) private readonly rowDetail: RowDetailService,
    @Inject(FieldCatalogService)
    private readonly fieldCatalog: FieldCatalogService,
    private readonly store: WorkbenchStoreService,
    private readonly preferences: UserPreferencesService,
  ) {
    // switchMap, so a fast re-selection cancels the in-flight request and only the latest anchor's
    // detail can reach the table.
    this.vm$ = this.store
      .anchorSelection()
      .pipe(switchMap(anchor => this.resolve(anchor)))

    // A fresh position drops the group / hide-empty / search state left over from the previous one.
    // skip(1) because the initial anchor has nothing to reset from, and keyed on the anchor's rowId
    // rather than anchorSelection()'s reference equality so a same-position refetch (which hands
    // back a new WorkbenchRow object for the same id) refreshes the data without throwing away the
    // view state for a position the user never navigated away from.
    this.store
      .anchorSelection()
      .pipe(
        skip(1),
        distinctUntilChanged((a, b) => anchorKeyOf(a) === anchorKeyOf(b)),
        takeUntil(this.destroy$),
      )
      .subscribe(() => this.filters$.next(defaultDetailFilters()))

    this.display$ = combineLatest([this.vm$, this.filters$]).pipe(
      map(([vm, filters]) => buildDisplayVm(vm, filters)),
      // Before the template's async pipe hands the rows to `[data]`, so the counts are already in
      // place when the resulting MODEL_UPDATED refreshes the two status panels.
      tap(display => this.syncGridContext(display)),
    )
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
    this.gridApi?.destroy()
  }

  private resolve(anchor: AnchorSelection): Observable<DetailViewModel> {
    // Nothing is loaded from here on unless a fetch is actually issued below, so the export name
    // must not outlive the table it described.
    this.exportTarget = undefined

    if (anchor.kind === 'none') return of(cardVm(subPanelEmptyState()))
    if (anchor.kind === 'multiple') {
      return of(cardVm(subPanelMultipleSelectedState()))
    }

    const source = rowDetailSourceOf(anchor.row.origin)
    // TODO(PVAL-26156): Clearwater rows have no row-detail endpoint — the flex-positions store holds
    // ENFUSION/PROMETHEUS rows only, so there is nothing to call. Confirm with the PVAL-26156
    // backend owners whether CW will be added; a decision request is raised on that ticket and the
    // user-facing consequence is documented in libs/panels/positions-2/README.md. Until it is
    // answered, showing the notice and issuing no request is the intended behaviour, not a gap.
    // Tagged with PVAL-26156, not PVAL-26293: the decision belongs to the backend ticket, and
    // tagging it with the ticket that ships this code would point the reader back at itself.
    if (source === null) return of(unavailableVm())

    return concat(of(loadingVm()), this.load(source, anchor.row))
  }

  private load(
    source: RowDetailSource,
    row: WorkbenchRow,
  ): Observable<DetailViewModel> {
    this.exportTarget = { source, rowId: row.rowId }
    return combineLatest([
      this.rowDetail.getRowDetail(source, row.rowId),
      this.catalogFor(source),
    ]).pipe(
      map(([entries, catalog]) => ({
        ...inertVm(),
        fieldSet: buildDetailFields(entries, catalog),
      })),
      // One failed fetch must not break the tab.
      catchError(() => of(errorVm())),
    )
  }

  /**
   * The anchor row's own field catalog, for group labels and value types. Already cached per
   * (account, source) by the shared service, so this reads that cache rather than refetching.
   *
   * A missing account or a failed catalog resolves to `null`, which the view model tolerates by
   * falling back to the shared group-label map and value-shape sniffing — a catalog problem must
   * degrade the labels, never blank the table.
   */
  private catalogFor(
    source: RowDetailSource,
  ): Observable<SourceFieldCatalog | null> {
    const accountId = this.store.accountIdValue()
    if (accountId == null) return of(null)
    return this.fieldCatalog
      .getCatalog(accountId, source)
      .pipe(catchError(() => of<SourceFieldCatalog | null>(null)))
  }

  // ---- toolbar ---------------------------------------------------------------

  onSearchChange(event: Event): void {
    const search = (event.target as HTMLInputElement).value
    this.filters$.next({ ...this.filters$.value, search })
  }

  onHideEmptyChange(event: Event): void {
    const hideEmpty = (event.target as HTMLInputElement).checked
    this.filters$.next({ ...this.filters$.value, hideEmpty })
  }

  /** Show/hide one group's rows. */
  toggleGroup(group: string): void {
    const hiddenGroups = new Set(this.filters$.value.hiddenGroups)
    if (hiddenGroups.has(group)) hiddenGroups.delete(group)
    else hiddenGroups.add(group)
    this.filters$.next({ ...this.filters$.value, hiddenGroups })
  }

  /** `Select All`: clears every hidden group, or hides all of them when they are already all on. */
  toggleAllGroups(groups: readonly string[]): void {
    const hiddenGroups = allGroupsShown(
      groups,
      this.filters$.value.hiddenGroups,
    )
      ? new Set(groups)
      : new Set<string>()
    this.filters$.next({ ...this.filters$.value, hiddenGroups })
  }

  isGroupShown(group: string): boolean {
    return !this.filters$.value.hiddenGroups.has(group)
  }

  /**
   * The kebab's one action. Exports what the table is showing as a real `.xlsx` workbook, built in
   * the browser: ag-Grid's Excel export walks the DISPLAYED rows and columns, so the group filter,
   * the hide-empty toggle, the search box and the current sort all carry through without this
   * having to re-apply any of them, and the values are the formatted screen text.
   *
   * Cell types are pinned per cell through `cellClass` + `excelStyles` (see createColumnDefs):
   * ag-Grid's xlsx writer would otherwise turn every numeric-looking string — ids, CUSIPs,
   * leading-zero codes — into a Number cell. `autoConvertFormulas` is deliberately left off so a
   * value starting with `=` stays text.
   *
   * Does nothing when the filters leave no rows on screen (`Fields: 0`); the menu item is disabled
   * then too, and this guard keeps a programmatic call from writing an empty workbook.
   */
  exportToExcel(): void {
    if (this.gridContext.fields === 0) return
    this.gridApi?.exportDataAsExcel({
      fileName: this.exportFileName(),
      sheetName: EXPORT_SHEET_NAME,
    })
  }

  private exportFileName(): string {
    if (!this.exportTarget) return 'position-detail.xlsx'
    // Enfusion row ids embed `|` (e.g. `144596|107532-82594|2026-08-11`), which is not a legal
    // Windows filename character, so anything outside word/dot/dash collapses to a dash.
    const rowId = this.exportTarget.rowId.replace(/[^\w.-]+/g, '-')
    return `position-detail-${this.exportTarget.source}-${rowId}.xlsx`
  }

  /** Stable identity for the group *ngFor loops. */
  trackByGroup(_index: number, group: string): string {
    return group
  }

  /** Mutated, never replaced: the status panels hold a reference to this object. */
  private syncGridContext(display: DetailDisplayViewModel): void {
    this.gridContext.fields = display.shownCount
    this.gridContext.total = display.total
  }

  // ---- grid ------------------------------------------------------------------

  private createGridOptions(): GridOptions {
    return {
      ...heliosClientSideRowConfig,
      defaultColDef: {
        ...heliosClientSideRowConfig.defaultColDef,
        sortable: true,
        resizable: true,
        // Header click cycles asc -> desc -> unsorted, per the prototype's tri-state
        // `detailSort` (`docs/Positions 2.0.dc.html:2292`).
        sortingOrder: ['asc', 'desc', null],
        // Column filters are deliberately off: the toolbar's group dropdown, hide-empty toggle and
        // search box are this table's filtering surface, and a second per-column filter UI would
        // give two independent answers to "why is this row missing".
        filter: false,
        // Long values must not wrap — the cell ellipsises (see the scss) and the full text is
        // carried in the browser tooltip (`enableBrowserTooltips` is on in the shared config).
        tooltipValueGetter: ({ value }) => (value == null ? '' : String(value)),
        // Numeric-looking values sort numerically, everything else numeric-aware-locale (prototype
        // `:2295`).
        comparator: compareDetailCells,
      },
      columnDefs: this.createColumnDefs(),
      // Matched against each cell's classes by the Excel export — see DETAIL_EXCEL_STYLES.
      excelStyles: DETAIL_EXCEL_STYLES,
      // `Fields` / `Total` live in the grid's own footer rather than a strip below it, so they read
      // as part of the table — the same place every other Helios grid puts its counts.
      context: this.gridContext,
      statusBar: detailStatusBarPanel,
      onGridReady: ({ api }) => {
        this.gridApi = api
        // Cell-range selection comes from heliosClientSideRowConfig (enableRangeSelection); this is
        // what makes Ctrl/Cmd-C over a range copy what the user actually sees.
        enableHeliosCopyBehavior(api)

        // Row and header heights follow the same density preference (Compact by default) as the two
        // grids above — see workbench-grid.component.ts. Without it this table keeps ag-Grid's
        // untuned, taller default rows, so it reads as a different table from the grids it belongs
        // to and fits far fewer fields on screen than the design shows.
        this.preferences
          .getDensityLevel()
          .pipe(takeUntil(this.destroy$))
          .subscribe(level =>
            enableHeliosGridDensityLevel(api, level === DensityLevel.Compact),
          )
      },
    }
  }

  /** Three columns at the design's initial widths, all sortable and resizable. */
  private createColumnDefs(): ColDef[] {
    return [
      {
        field: 'group',
        headerName: 'Column Group',
        width: 180,
        // The group reads as secondary metadata beside the field it labels, per the design.
        cellClass: ['detail-cell', 'detail-cell--secondary', XL_TEXT_CLASS],
      },
      {
        field: 'name',
        headerName: 'Column Name',
        width: 280,
        cellClass: ['detail-cell', XL_TEXT_CLASS],
      },
      {
        field: 'value',
        headerName: 'Value',
        // Takes the width the first two columns leave, so the three of them span the table. Sized
        // with `width: 320` it stopped short and ag-Grid filled the remainder with its hatched
        // no-column area — a wide dead zone beside every value. `minWidth` keeps the design's
        // starting width when the panel is narrow, where the grid scrolls horizontally instead.
        flex: 1,
        minWidth: 320,
        // Left-aligned like the other two: the design keeps all three columns left-aligned, and a
        // right-aligned numeric column would read as an amount even for the id fields.
        // The second class types the exported cell: Number only when the view model rendered it as
        // a finite number, Text for everything else.
        cellClass: detailValueCellClass,
      },
    ]
  }
}
